import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './BoardDelayedClickUpTooltip.css';

const DEFAULT_DELAY_MS = 1000;
const TOOLTIP_GAP = 10;
const TOOLTIP_COLUMN_PADDING = 10;
const TOOLTIP_WIDTH = 292;
/** Largura estimada para texto curto (compact); reajusta após medir o DOM. */
const TOOLTIP_WIDTH_COMPACT = 148;

function getBoundaryRect(anchorEl) {
  const column = anchorEl?.closest?.('.kanban-column.technician-column');
  if (column) return column.getBoundingClientRect();
  return {
    left: TOOLTIP_COLUMN_PADDING,
    right: window.innerWidth - TOOLTIP_COLUMN_PADDING,
  };
}

function computeTooltipCoords(anchorRect, boundaryRect, width) {
  const half = width / 2;
  const minLeft = boundaryRect.left + TOOLTIP_COLUMN_PADDING + half;
  const maxLeft = boundaryRect.right - TOOLTIP_COLUMN_PADDING - half;
  const idealLeft = anchorRect.left + anchorRect.width / 2;
  const left = Math.min(Math.max(idealLeft, minLeft), maxLeft);
  const top = anchorRect.bottom + TOOLTIP_GAP;
  const anchorCenter = anchorRect.left + anchorRect.width / 2;
  const tooltipLeftEdge = left - half;
  const arrowLeft = anchorCenter - tooltipLeftEdge;

  return {
    top,
    left,
    arrowLeft,
  };
}

/**
 * Tooltip estilo ClickUp (fundo branco + seta) com atraso antes de exibir.
 * Renderizado em portal para não ser cortado por overflow da coluna.
 * @param {boolean} [compact] — ajusta largura ao texto (sem largura fixa 292px)
 */
export function BoardDelayedClickUpTooltip({
  text,
  placement = 'bottom',
  delayMs = DEFAULT_DELAY_MS,
  className = '',
  dismissOnInteract = false,
  compact = false,
  children,
}) {
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [tipCoords, setTipCoords] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const defaultWidth = compact ? TOOLTIP_WIDTH_COMPACT : TOOLTIP_WIDTH;

  const readCoords = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return null;
    const anchorRect = anchor.getBoundingClientRect();
    const boundaryRect = getBoundaryRect(anchor);
    const measured = tipRef.current?.offsetWidth;
    const width = measured > 0 ? measured : defaultWidth;
    return computeTooltipCoords(anchorRect, boundaryRect, width);
  }, [defaultWidth]);

  const refreshCoords = useCallback(() => {
    const coords = readCoords();
    if (coords) setTipCoords(coords);
  }, [readCoords]);

  const scheduleShow = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      const coords = readCoords();
      if (coords) {
        setTipCoords(coords);
        setVisible(true);
      }
    }, delayMs);
  }, [clearTimer, delayMs, readCoords]);

  const hide = useCallback(() => {
    clearTimer();
    setVisible(false);
    setTipCoords(null);
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  useLayoutEffect(() => {
    if (!visible) return undefined;
    refreshCoords();
    window.addEventListener('scroll', refreshCoords, true);
    window.addEventListener('resize', refreshCoords);
    return () => {
      window.removeEventListener('scroll', refreshCoords, true);
      window.removeEventListener('resize', refreshCoords);
    };
  }, [visible, refreshCoords, text, compact]);

  const anchorClass = ['board-delayed-clickup-tooltip-anchor', className].filter(Boolean).join(' ');

  const tipStyle = tipCoords
    ? {
        top: tipCoords.top,
        left: tipCoords.left,
        '--tooltip-arrow-left': `${tipCoords.arrowLeft}px`,
      }
    : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={anchorClass}
        onMouseEnter={scheduleShow}
        onMouseLeave={hide}
        onFocus={scheduleShow}
        onBlur={hide}
        onPointerDown={dismissOnInteract ? hide : undefined}
        onClick={dismissOnInteract ? hide : undefined}
      >
        {children}
      </span>
      {visible && tipCoords
        ? createPortal(
            <span
              ref={tipRef}
              className={[
                'board-delayed-clickup-tooltip-popover',
                'board-clickup-tooltip',
                `board-clickup-tooltip--${placement}`,
                compact ? 'board-clickup-tooltip--compact' : 'board-clickup-tooltip--multiline',
              ].join(' ')}
              style={tipStyle}
              role="tooltip"
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
